import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { requestLoginCode, verifyLoginCode, verifyToken, refreshToken, type User } from '../api/auth';
import SessionWarningModal from '../components/SessionWarningModal';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, turnstileToken: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const expiryEpochSecRef = useRef<number | null>(null);
  const warnTimeoutRef = useRef<number | null>(null);
  const logoutTimeoutRef = useRef<number | null>(null);

  // Production: warn at 5 minutes before expiry
  const WARNING_THRESHOLD_SECONDS = 300;

  const getTokenExpiry = (token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload.exp === 'number') return payload.exp;
      return null;
    } catch {
      return null;
    }
  };

  const clearTimers = () => {
    if (warnTimeoutRef.current) {
      window.clearTimeout(warnTimeoutRef.current);
      warnTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      window.clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  };

  const scheduleSessionTimers = (token: string) => {
    const exp = getTokenExpiry(token);
    if (!exp) return;

    expiryEpochSecRef.current = exp;
    clearTimers();
    setShowWarning(false);

    const nowSec = Math.floor(Date.now() / 1000);
    const secondsUntilWarn = exp - WARNING_THRESHOLD_SECONDS - nowSec;
    const secondsUntilLogout = exp - nowSec;

    if (secondsUntilWarn <= 0) {
      setShowWarning(true);
    } else {
      warnTimeoutRef.current = window.setTimeout(() => {
        setShowWarning(true);
      }, secondsUntilWarn * 1000);
    }

    if (secondsUntilLogout > 0) {
      logoutTimeoutRef.current = window.setTimeout(() => {
        // Expire session
        localStorage.removeItem('authToken');
        setUser(null);
        setShowWarning(false);
        clearTimers();
      }, secondsUntilLogout * 1000);
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';

  // Function to refresh user data from server
  const refreshUser = async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const response = await verifyToken(token);
        if (response.valid) {
          setUser(response.user);
        } else {
          localStorage.removeItem('authToken');
          setUser(null);
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem('authToken');
        setUser(null);
      }
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const response = await verifyToken(token);
          if (response.valid) {
            setUser(response.user);
            scheduleSessionTimers(token);
          } else {
            localStorage.removeItem('authToken');
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('authToken');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, turnstileToken: string) => {
    try {
      await requestLoginCode({ email, turnstileToken });
    } catch (error) {
      throw error;
    }
  };

  const verifyCode = async (email: string, code: string) => {
    try {
      const response = await verifyLoginCode({ email, code });
      localStorage.setItem('authToken', response.token);
      setUser(response.user);
      scheduleSessionTimers(response.token);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setShowWarning(false);
    clearTimers();
  };

  const handleStayLoggedIn = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return logout();
    try {
      const refreshed = await refreshToken(token);
      localStorage.setItem('authToken', refreshed.token);
      setUser(refreshed.user);
      setShowWarning(false);
      scheduleSessionTimers(refreshed.token);
    } catch (e) {
      // If refresh fails (token already expired), log out
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    verifyCode,
    logout,
    isAuthenticated,
    isAdmin,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showWarning && expiryEpochSecRef.current && (
        <SessionWarningModal
          expiryEpochSec={expiryEpochSecRef.current}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
};
